import { useCallback, useMemo, useState } from 'react'
import { IntegrationAccount } from '@phrasee/phrasee-typings'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { CepIntegration } from 'common/api'
import ActionIcon from 'common/components/ActionIcon'
import Button from 'common/components/button'
import SortMenu from 'common/components/sortMenu'
import {
  sortByOptions,
  SortOptions,
  SortOptionsValue,
} from 'common/components/sortMenu/SortMenu'
import Tooltip from 'common/components/Tooltip'
import { useLocalStorage } from 'common/hooks/useLocalStorage'
import { Grid, ListIcon } from 'common/icons'
import { Add } from 'common/icons'

import AccountsView from './AccountsView'
import IntegrationsDeleteModal from './IntegrationsDeleteModal'
import IntegrationsSearchbox from './IntegrationSearchbox'
import IntegrationsUpdateModal from './IntegrationsUpdateModal'

const ACCOUNTS_PAGE_VIEW_KEY = `accounts-page-view`

const sortOptionsAccount: SortOptions = [
  {
    label: 'Recent',
    value: {
      property: 'createdAt',
      isAscending: false,
    },
  },
  {
    label: 'Oldest',
    value: {
      property: 'createdAt',
      isAscending: true,
    },
  },
  {
    label: 'Ascending',
    value: { property: 'provider', isAscending: true },
  },
  {
    label: 'Descending',
    value: { property: 'provider', isAscending: false },
  },
]

const sortOptionsAccountIntegrations: SortOptions = [
  {
    label: 'Recent',
    value: {
      property: 'created_at',
      isAscending: false,
    },
  },
  {
    label: 'Oldest',
    value: {
      property: 'created_at',
      isAscending: true,
    },
  },
  {
    label: 'Ascending',
    value: { property: 'name', isAscending: true },
  },
  {
    label: 'Descending',
    value: { property: 'name', isAscending: false },
  },
]

type Props = {
  accounts: IntegrationAccount[] | CepIntegration[]
  onAddClick: () => void
}

const AccountsPage = ({ accounts, onAddClick }: Props) => {
  const { simplifiedProjectIntegrations: isSimplifiedProjectIntegrationOn } =
    useFlags()

  const [integrationAccountToDelete, setIntegrationAccountToDelete] =
    useState<IntegrationAccount | null>(null)
  const [integrationAccountToUpdate, setIntegrationAccountToUpdate] =
    useState<IntegrationAccount | null>(null)

  const [isTableView, setIsTableView] = useLocalStorage(
    ACCOUNTS_PAGE_VIEW_KEY,
    false
  )

  const [sortOptions, setSortOptions] = useState<SortOptionsValue>({
    property: 'createdAt',
    isAscending: false,
  })

  const [sortOptionsCep, setSortOptionsCep] = useState<SortOptionsValue>({
    property: 'created_at',
    isAscending: false,
  })
  const [searchValue, setSearchValue] = useState<string>('')

  const filterIntegrationAccountByName = useCallback(
    (account: CepIntegration) =>
      account.name
        .replaceAll(' ', '')
        .toLowerCase()
        .includes(searchValue.replaceAll(' ', '').toLowerCase()),
    [searchValue]
  )

  const filterByName = useCallback(
    ({ provider }: IntegrationAccount) =>
      provider
        .replaceAll(' ', '')
        .toLowerCase()
        .includes(searchValue.replaceAll(' ', '').toLowerCase()),
    [searchValue]
  )

  const filteredAccounts = useMemo(() => {
    if (isSimplifiedProjectIntegrationOn) {
      const filteredData = ((accounts as CepIntegration[]) ?? []).filter(
        filterIntegrationAccountByName
      )
      const sortedData = sortByOptions(filteredData, sortOptionsCep)
      return sortedData
    }
    const filteredData = ((accounts as IntegrationAccount[]) ?? []).filter(
      filterByName
    )
    const sortedData = sortByOptions(filteredData, sortOptions)
    return sortedData
  }, [
    isSimplifiedProjectIntegrationOn,
    accounts,
    filterByName,
    sortOptions,
    filterIntegrationAccountByName,
    sortOptionsCep,
  ])

  const searchOptions = useMemo(() => {
    const accountsList = accounts ?? []
    return isSimplifiedProjectIntegrationOn
      ? (accountsList as CepIntegration[]).map((account) => ({
          value: account._id ?? '',
          label: account.name,
        }))
      : (accountsList as IntegrationAccount[]).map((account) => ({
          value: account.id ?? '',
          label: account.provider,
        }))
  }, [accounts, isSimplifiedProjectIntegrationOn])

  return (
    <>
      <div className="flex items-center my-8 self-end">
        <ActionIcon
          aria-label="Toggle view button"
          onClick={() => setIsTableView(!isTableView)}
        >
          {isTableView ? (
            <Tooltip overlay="Tile view">
              <Grid />
            </Tooltip>
          ) : (
            <Tooltip overlay="List view">
              <ListIcon />
            </Tooltip>
          )}
        </ActionIcon>
        <Tooltip overlay="Sort">
          <SortMenu
            className="text-base pt-3"
            sortOptions={
              isSimplifiedProjectIntegrationOn
                ? sortOptionsAccountIntegrations
                : sortOptionsAccount
            }
            onSortClick={
              isSimplifiedProjectIntegrationOn
                ? setSortOptionsCep
                : setSortOptions
            }
          />
        </Tooltip>
        <Button
          variant="primary"
          prefixIcon={<Add isDefaultColor={false} />}
          onClick={onAddClick}
        >
          Integration
        </Button>
        <IntegrationsSearchbox
          placeholder="Find integration"
          data-cy="integrations-search-box"
          data-testid="integrations-search-box"
          onSearch={setSearchValue}
          options={searchOptions}
        />
      </div>
      <AccountsView
        accounts={filteredAccounts}
        isTableView={isTableView}
        isSimplifiedProjectIntegrationOn={isSimplifiedProjectIntegrationOn}
        onUpdate={(integrationAccount) =>
          setIntegrationAccountToUpdate(
            integrationAccount as IntegrationAccount
          )
        }
        onDelete={(integrationAccount) =>
          setIntegrationAccountToDelete(
            integrationAccount as IntegrationAccount
          )
        }
      />
      {!!integrationAccountToUpdate && (
        <IntegrationsUpdateModal
          accountId={integrationAccountToUpdate.accountId}
          integrationId={integrationAccountToUpdate.id}
          label={integrationAccountToUpdate.label ?? ''}
          accountName={integrationAccountToUpdate.accountName}
          logoUrl={integrationAccountToUpdate.logoUrl}
          onCancel={() => {
            setIntegrationAccountToUpdate(null)
          }}
          onClose={() => {
            setIntegrationAccountToUpdate(null)
          }}
        />
      )}
      {!!integrationAccountToDelete && (
        <IntegrationsDeleteModal
          accountId={integrationAccountToDelete.accountId}
          integrationId={integrationAccountToDelete.id}
          onSuccessDelete={() => {
            setIntegrationAccountToDelete(null)
          }}
          onCancel={() => {
            setIntegrationAccountToDelete(null)
          }}
        />
      )}
    </>
  )
}

export default AccountsPage
